/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { canadianPayrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Save, Palmtree } from 'lucide-react';

/**
 * VacationBalanceCard — Canadian vacation accrual tracker.
 *
 * Shows the four key numbers (accrual rate, auto-suggested, manual
 * adjustment, actual balance) and lets a manager override them.
 *
 * NEVER writes implicitly. Every change requires the manager to click Save.
 * Hides itself when the employee is not CA-eligible (API returns 403).
 */
export function VacationBalanceCard({ employeeId, canEdit = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  const [rate, setRate] = useState('0.04');
  const [adj, setAdj] = useState('0');
  const [used, setUsed] = useState('0');

  const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const load = useCallback(async () => {
    if (!employeeId) return;
    try {
      const r = await canadianPayrollAPI.vacation(employeeId);
      setData(r.data);
      setRate(String(r.data.accrual_rate ?? 0.04));
      setAdj(String(r.data.manual_adjustment ?? 0));
      setUsed(String(r.data.used ?? 0));
    } catch (err) {
      if (err.response?.status === 403) setHidden(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const rateNum = parseFloat(rate);
    const adjNum  = parseFloat(adj);
    const usedNum = parseFloat(used);
    if (!Number.isFinite(rateNum) || rateNum < 0 || rateNum > 0.5) {
      toast.error('Accrual rate must be between 0 and 0.5'); return;
    }
    if (!Number.isFinite(adjNum)) { toast.error('Manual adjustment must be a number'); return; }
    if (!Number.isFinite(usedNum) || usedNum < 0) { toast.error('Used must be a non-negative number'); return; }
    setSaving(true);
    try {
      const r = await canadianPayrollAPI.updateVacation(employeeId, {
        accrual_rate: rateNum,
        manual_adjustment: adjNum,
        used: usedNum,
      });
      setData(r.data);
      toast.success('Vacation balance updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (hidden) return null;

  return (
    <Card className="border-slate-200" data-testid="vacation-balance-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Palmtree className="text-teal-600" size={16} />
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Vacation Pay
          </h3>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Cell label="Auto-suggested" value={fmt(data.auto_suggested_accrued)} hint={`@ ${(data.accrual_rate * 100).toFixed(1)}%`} />
              <Cell label="Manual Adj."    value={fmt(data.manual_adjustment)} hint={data.manual_adjustment >= 0 ? 'added' : 'subtracted'} />
              <Cell label="Used"           value={fmt(data.used)} />
              <Cell label="Balance"        value={fmt(data.balance)} accent />
            </div>

            {canEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
                <Field
                  label="Accrual Rate (decimal)"
                  testid="vacation-rate-input"
                  value={rate}
                  onChange={setRate}
                  placeholder="0.04"
                  presets={[0.04, 0.06, 0.08]}
                  onPreset={(v) => setRate(String(v))}
                />
                <Field
                  label="Manual Adjustment ($)"
                  testid="vacation-manual-adj-input"
                  value={adj}
                  onChange={setAdj}
                  placeholder="0"
                />
                <Field
                  label="Used ($)"
                  testid="vacation-used-input"
                  value={used}
                  onChange={setUsed}
                  placeholder="0"
                />
                <div className="col-span-full">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="vacation-save-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Vacation Override
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    Manager override only. Auto-suggested accrual is calculated from YTD gross × rate. Manual adjustments and "used" values are stored verbatim — never auto-written.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Cell({ label, value, hint, accent = false }) {
  return (
    <div className={`rounded-md p-3 ${accent ? 'bg-teal-50 text-teal-800' : 'bg-slate-50 text-slate-700'}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-base font-bold tabular-nums mt-0.5">{value}</p>
      {hint && <p className="text-[10px] opacity-60 mt-0.5">{hint}</p>}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, presets, onPreset, testid }) {
  return (
    <div>
      <label className="text-xs text-slate-600 font-medium block mb-1">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
      />
      {presets && (
        <div className="flex gap-1.5 mt-1.5">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPreset(p)}
              className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid={`vacation-rate-preset-${p}`}
            >
              {Math.round(p * 100)}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default VacationBalanceCard;
